export default class EngravingAPI {
    constructor(useHiddenImage = false) {
        // ========================
        // Properties
        // ========================
        this.container = !useHiddenImage
            ? document.querySelector('#modal_product__image')
            : document.querySelector('#hidden_product__image')

        this.doodleElement = null
        this.productCustom = []
        this.configuratorSetup = {}
        this.events = {ready: null}
    }

    // ========================
    // Methods
    // ========================
    addEvents() {
        this.events.ready = new Event('readyCheck', {bubbles: true, cancelable: true})
    }

    loadScript(url, onload) {
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.onload = onload
        script.src = url
        document.head.appendChild(script)
    }

    init(setup, code) {
        this.setup = setup
        this.addEvents()

        this.loadScript(setup.clientRoot, () => {
            window.engravingController = null

            // create controller
            this.controller = new window.DriveCustomizer.Controller({
                blueprint: this.setup.blueprintUrl
            })
            this.controller.load()

            this.controller.on('afterInitializeBlueprint', () => {
                this.translateBlueprint()

                if (code != 'null') {
                    this.updateImageCode(
                        'Product',
                        this.getPlCode(
                            this.configuratorSetup.groupPlacements['Product'].components,
                            code
                        )
                    )
                }

                // create image
                this.image = new window.DriveCustomizer.ImageView(this.controller, {
                    container: this.container
                })

                this.container.dispatchEvent(this.events.ready)
                window.engravingImage = this.image
            })

            window.engravingController = this.controller
        })
    }

    translateBlueprint() {
        this.configuratorSetup.options = {}
        this.controller.blueprint.options.forEach((element) => {
            this.configuratorSetup.options[element.code] = {
                code: element.code,
                name: element.name,
                definition: element.definition,
                custom: element.custom
            }
        })

        this.configuratorSetup.groupPlacements = {}
        this.controller.blueprint.groups[0].placements.forEach((element) => {
            const placement = {
                code: element.code,
                name: element.name,
                definition: element.definition,
                custom: element.custom,
                components: element.components,
                isOfType: ''
            }

            if (element.code.indexOf('TEXT') !== -1) {
                placement.isOfType = 'TEXT'
                placement.fonts = {}
                placement.sizes = {}
                placement.lines = Number(element.components[0]?.custom?.['widget-multiline']) || 0

                element.components.forEach((component) => {
                    const [codeOrientation, codeFontFamily, codeFontSize] =
                        component.code.split('-')

                    placement.isHorizontal = codeOrientation.indexOf('HORIZONTAL') !== -1

                    if (!placement.fonts[codeFontFamily]) {
                        placement.fonts[codeFontFamily] = {
                            code: codeFontFamily,
                            font: component.custom['personalization-font'],
                            alignment: component.custom['personalization-alignment']
                        }
                        component.description?.forEach((desc) => {
                            if (desc.code === codeFontFamily) {
                                placement.fonts[codeFontFamily].fontFamilyName = desc.name
                            }
                        })
                    }

                    if (!placement.sizes[codeFontSize]) {
                        placement.sizes[codeFontSize] = {
                            code: codeFontSize,
                            size: component.custom['personalization-size'],
                            maxlength: component.custom['ks-max-length']
                        }
                    }
                })
            }

            if (element.code.indexOf('Product') !== -1) {
                this.productCustom = element.components
            }

            this.configuratorSetup.groupPlacements[element.code] = placement
        })

        this.configuratorSetup.views = {}
        this.controller.blueprint.views.forEach((view) => {
            if (view.placements?.length) {
                this.configuratorSetup.views[view.code] = {
                    code: view.code,
                    name: view.name,
                    viewType: view.viewType,
                    frameLow: view.frameLow,
                    background: view.background,
                    bakeInRecipe: view.bakeInRecipe,
                    onlySelectedPlacements: view.onlySelectedPlacements,
                    custom: view.custom,
                    placements: view.placements.map((placement) => ({
                        ...placement,
                        custom:
                            placement.code === 'Product'
                                ? this.productCustom
                                : this.configuratorSetup.groupPlacements[placement.code]?.custom
                    }))
                }
            }
        })
    }

    selectView(viewCode) {
        this.image?.selectViewByCode(viewCode)
    }

    updateText(code, text) {
        this.controller?.updatePersonalization(code, text)
    }

    updateFontFamily(code, fontFamily) {
        this.controller?.commitCustomAttributes(code, {
            'personalization-font': fontFamily
        })
    }

    updateFontSize(code, fontSize) {
        this.controller?.commitCustomAttributes(code, {
            'personalization-size': fontSize
        })
    }

    updateImageCode(code, driveCommercePlacementIndependentCode) {
        this.controller?.updateComponent(code, driveCommercePlacementIndependentCode)
    }

    getCurrentViewURL(view) {
        return this.controller?.makeFrameUrl(
            view,
            this.controller.state,
            this.image?.selectedFrame()
        )
    }

    save() {
        this.controller?.saveRecipe()
    }

    dataURLtoBlob(dataURL) {
        const binary = atob(dataURL.split(',')[1])
        const array = []
        for (let i = 0; i < binary.length; i++) {
            array.push(binary.charCodeAt(i))
        }
        return new Blob([new Uint8Array(array)], {
            type: 'image/png'
        })
    }

    getPlCode(array, code) {
        const found = array.find((e) => e.placementIndependentCode === code)
        return found?.dynamicCode
    }

    async fetchReceipeData(recipeUrl, recipeId) {
        try {
            const response = await fetch(`${recipeUrl}${recipeId}.json`, {
                method: 'GET'
            })

            return await response.json()
        } catch (e) {
            console.error('Error fetching recipe data:', e.message)
            return null
        }
    }
}
